export interface CreditAnalysis {
    id: string;
    uuid: string;
    batchNumber?: string;
    date: string;
    assignor: string;
    drawee: string;
    document: string;
    root: string;
    type: string;
    year: string;
    city: string;
    state: string;
    qsa: string;
    lcApproved: string;
    annualRevenue: string;
    equity: string;
    rating: string;
    recommendation: string;
    status: string;
    approvedValue: string;
    suggestedValue: string;
    approvedPercent: string;
    justification: string;
    originalValue: string;
    dupPercent: string;
    creditObs: string;
    currentExposure: string;
    potentialExposure: string;
    previous: string;
    approvedAmount: string;
    scrTotal: string;
    scrRural: string;
    overdue: string;
    scrPrejuizo: string;
    score: string;
    agroScore: string;
    agroScoreConsulted?: boolean;
    pdef: string;
    pdefAgro: string;
    scoreQuod: string;
    scoreBoaVista: string;
    restriction: string;
    restrictions: { type: string; count: number; balance: number }[];
    obs: string;
    ccirHa: string;
    ccirValue: string;
    patrimonioScr: string;
    ccirParticipation: string;
    ccirPatrimSacado: string;
    ccirPatrimDividaGrupo: string;
    cprNumber: string;
    pafDate: string;
    economicGroup: string;
    reasons: string[];
    // AI Analysis (merged from getAiAnalysisResults)
    aiRecommendation?: AiRecommendation | null;
    aiConfidence?: number | null;
    aiPdefault1y?: number | null;
    aiRationale?: string | null;
    aiRiskFactors?: string[];
    aiPositiveFactors?: string[];
    aiErrorMessage?: string | null;
}


// ── CCIR Types ──

export interface CcirOwner {
    document: string;
    name: string;
    participationPercent: number;
    isDrawee: boolean;
    condition?: string;
    itemId?: string;
    scrTotal?: number;
    scrVencido?: number;
    scrPrejuizo?: number;
    pdefault?: number;
    agroScore?: number;
    patrimony?: number;
    patrimonyDebtRatio?: number;
    bureauData?: {
        scr?: { creditoTotal: string; creditoRural: string; creditoVencido: string; creditoPrejuizo: string };
        ccir?: { totalArea: number; totalValue: number; propertyCount: number };
        acriscore?: { score: number | null; defaultRate: string; range: string };
        serasa?: { score: number | null; agroscore: number | null; pdef: string; agroscorePdef: string };
    };
    bureauErrors?: string[];
}

export interface CcirProperty {
    id: string;
    name: string;
    area: number;
    valorHa: number;
    value: number;
    city: string;
    state: string;
    agroconsultRegion: string;
    owners: CcirOwner[];
}

export interface CcirGroupMetrics {
    groupPatrimony: number;
    groupDebt: number;
    groupPatrimonyDebtRatio: number;
    draweeParticipation: number;
    draweePatrimony: number;
}

export interface CcirDetailsResponse {
    itemId: string;
    draweeDocument: string;
    draweeName: string;
    properties: CcirProperty[];
    coOwners: CcirOwner[];
    consolidatedParticipation: number;
    deepAnalysisCompleted: boolean;
    deepAnalysisBatchId: string | null;
    groupMetrics: CcirGroupMetrics | null;
}


// ── AI Analysis Types ──

export const AiRecommendation = {
    APPROVE: 'APPROVE',
    REJECT: 'REJECT',
    REVIEW: 'REVIEW',
} as const satisfies Record<string, string>;

export type AiRecommendation = typeof AiRecommendation[keyof typeof AiRecommendation];

export interface AiAnalysisProgress {
    total: number;
    completed: number;
    pending: number;
    failed: number;
}

export interface AiAnalysisResultItem {
    analysisItemId: string;
    document: string;
    entityName: string;
    recommendation: AiRecommendation;
    confidence: number;
    pdefault1y: number | null;
    rationale: string;
    riskFactors: string[];
    positiveFactors: string[];
    processingTimeMs: number;
    errorMessage: string | null;
    createdAt: string | null;
}

export interface AiAnalysisResponse {
    batchId: string;
    status: string;
    statusFormatted: string;
    progress: AiAnalysisProgress;
    results: AiAnalysisResultItem[];
}

export interface AiAnalysisDispatchResponse {
    batchId: string;
    itemsDispatched: number;
    itemsSkipped: number;
    message: string;
}

// ── Batch Processing Types ──

export type BureauStatus = 'approved' | 'rejected' | 'pending' | 'not_processed' | 'error';

export interface BureauResult {
    bureauName: string;
    bureauCode: string;
    status: BureauStatus;
    processedAt?: string;
    reason?: string;
}

export interface DraweeDetail {
    id: string;
    name: string;
    document: string;
    pafDate?: string;
    errorMessage?: string | null;
    bureauResults: BureauResult[];
}

export interface BatchProcessing {
    id: string;
    uuid: string;
    batchNumber: string;
    batchName: string;
    status: 'processing' | 'error' | 'completed';
    createdAt: string;
    assignors: {
        name: string;
        drawees: {
            name: string;
            document: string;
            status: 'approved' | 'pending' | 'rejected';
        }[];
    }[];
}
